import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useId, useState, type MouseEvent } from 'react';

export interface RowAction {
  /** The words on the menu item. Also how a test finds it. */
  label: string;
  onSelect: () => void;
  /** Greys the item out and stops it being chosen. */
  disabled?: boolean;
}

interface RowActionsMenuProps {
  /**
   * What this row is about - an organization's name, a person's name. It goes
   * into the control's accessible name, so a screen reader says whose actions
   * these are rather than reading "Actions" forty times down a table.
   */
  rowLabel: string;
  actions: RowAction[];
}

/**
 * The actions for one row of a table, behind one control.
 *
 * Shared rather than per-feature because two sections need the same thing and
 * neither may import from the other: the organizations list uses it for Open
 * and Change status, and the users list for the things done to a person.
 *
 * MUI's Menu carries the keyboard behaviour the pattern requires: the arrow
 * keys move between items, Escape closes the menu, and focus returns to the
 * control that opened it.
 */
export function RowActionsMenu({ rowLabel, actions }: RowActionsMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  const menuId = `${id}-menu`;
  const buttonId = `${id}-button`;
  const open = anchor !== null;

  function choose(action: RowAction) {
    // Close first. The item's own work may open a dialog, and focus has to be
    // back on the button before that happens or it lands nowhere.
    setAnchor(null);
    action.onSelect();
  }

  return (
    <>
      <IconButton
        id={buttonId}
        aria-label={`Actions for ${rowLabel}`}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? true : undefined}
        size="small"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        // Names the menu after the row too, so the list a screen reader opens
        // is tied to the row it came from.
        slotProps={{ list: { 'aria-labelledby': buttonId } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            onClick={() => choose(action)}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
